import { useEffect } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLogsDownload, api } from "../client";
import type { LogLevelFilter, LogRow, LogsResponse } from "../types";

export interface LogsQuery {
  level?: LogLevelFilter;
  q?: string;
  collapse_repeats?: boolean;
  bot_instance_id?: string;
  page_size?: number;
}

// Simple, non-paginated fetch — used by AppShell just to read `counts.error`
// for the nav badge, where cursor pagination would be overkill.
export function useLogs(query: LogsQuery) {
  return useQuery({
    queryKey: ["logs", query],
    queryFn: () =>
      api.get<LogsResponse>("/logs", {
        level: query.level ?? "all",
        q: query.q || undefined,
        collapse_repeats: query.collapse_repeats ?? true,
        bot_instance_id: query.bot_instance_id,
        page_size: query.page_size ?? 100,
      }),
    refetchInterval: 5000,
    placeholderData: (prev) => prev,
  });
}

// Cursor-paginated ("Загрузить ещё") log feed for the Logs page, following
// API_CONTRACT.md's `before_id` cursor exactly (rows with id < before_id) —
// pages are stitched into one flat, deduped row list.
export function useLogsInfinite(query: Omit<LogsQuery, "page_size">) {
  const queryClient = useQueryClient();
  const infiniteKey = ["logs-infinite", query] as const;

  const infinite = useInfiniteQuery({
    queryKey: infiniteKey,
    queryFn: ({ pageParam }) =>
      api.get<LogsResponse>("/logs", {
        level: query.level ?? "all",
        q: query.q || undefined,
        collapse_repeats: query.collapse_repeats ?? true,
        bot_instance_id: query.bot_instance_id,
        before_id: pageParam as number | undefined,
        page_size: 100,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more || lastPage.rows.length === 0) return undefined;
      return lastPage.rows[lastPage.rows.length - 1].id;
    },
    // Deliberately NOT refetchInterval here — react-query refetches an
    // infinite query's ENTIRE page chain on every tick, one request per
    // already-loaded page. Reported live: clicking "Загрузить ещё" once
    // meant 2 requests every 5s, twice meant 3, growing forever with more
    // history paged in ("каждый подгрузчик каждые несколько секунд их
    // скачивает"). The poll query below fetches only the newest page and
    // merges it by hand, keeping this at exactly one background request
    // per tick no matter how many pages are loaded.
  });

  // Polls just the newest page (before_id omitted) on its own fixed
  // interval, independent of how many "Load more" pages exist — see the
  // comment on `infinite` above for why this is separate from it.
  const poll = useQuery({
    queryKey: ["logs-poll", query],
    queryFn: () =>
      api.get<LogsResponse>("/logs", {
        level: query.level ?? "all",
        q: query.q || undefined,
        collapse_repeats: query.collapse_repeats ?? true,
        bot_instance_id: query.bot_instance_id,
        page_size: 100,
      }),
    refetchInterval: 5000,
  });

  // Merges the poll's result into the infinite query's own cache instead of
  // rendering it separately — Logs.tsx keeps reading `infinite.data` as
  // its single source of truth (pagination AND live updates), rather than
  // needing to reconcile two independent row lists itself.
  useEffect(() => {
    if (!poll.data) return;
    queryClient.setQueryData<{ pages: LogsResponse[]; pageParams: (number | undefined)[] }>(infiniteKey, (old) => {
      // Nothing paged in yet (still loading, or first mount racing this
      // same-tick poll) — let the infinite query's own fetch populate this
      // normally instead of racing it.
      if (!old || old.pages.length === 0) return old;

      const knownIds = new Set(old.pages.flatMap((p) => p.rows.map((r) => r.id)));
      const freshRows = poll.data.rows.filter((r) => !knownIds.has(r.id));
      // Only ever PREPEND to page 0 — its own last row (the cursor every
      // later page's pageParam was computed from) never moves, so already-
      // loaded pages 2+ stay valid with no gap or duplicate no matter how
      // many new rows just showed up at the top.
      const pages = [...old.pages];
      pages[0] = {
        ...pages[0],
        rows: freshRows.length > 0 ? [...freshRows, ...pages[0].rows] : pages[0].rows,
        // counts/top_error reflect the WHOLE filtered set (see logs.py —
        // only before_id=None's counts query is unscoped), so refresh them
        // from every poll tick even when no new row showed up at the top.
        counts: poll.data.counts,
        top_error: poll.data.top_error,
      };
      return { ...old, pages };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll.data]);

  return infinite;
}

export function flattenLogPages(pages: LogsResponse[] | undefined): LogRow[] {
  if (!pages) return [];
  const seen = new Set<number>();
  const rows: LogRow[] = [];
  for (const page of pages) {
    for (const row of page.rows) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
  }
  return rows;
}

export async function downloadLogs(query: Omit<LogsQuery, "page_size">): Promise<void> {
  const text = await fetchLogsDownload({
    level: query.level ?? "all",
    q: query.q || undefined,
    collapse_repeats: query.collapse_repeats ?? true,
    bot_instance_id: query.bot_instance_id,
  });
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bot-logs-${new Date().toISOString().slice(0, 10)}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
